import { Call } from '@gem/call/CallStore/Call';
import { getDefaultDevices, IDevices } from '@gem/call/tasks/getDefaultDevices';
import { debounce } from '@gem/utils';

import { CallProviderService } from '../CallProviderService';
import { MediaDeviceService } from './MediaDevicesService';

export class ChangeDeviceService {
  parent: CallProviderService;
  holdMicrophonePromise: Promise<MediaStream> | null = null; //this stream stored permission on microphone
  call: Call;
  devicesServices: {
    audioinput: MediaDeviceService;
    audiooutput: MediaDeviceService;
    videoinput: MediaDeviceService;
  };

  constructor(parent: CallProviderService, call: Call) {
    this.parent = parent;
    this.call = call;
    this.devicesServices = {
      audioinput: new MediaDeviceService({
        deviceKind: 'audioinput',
        handlers: {},
        call: this.call,
      }),
      videoinput: new MediaDeviceService({
        deviceKind: 'videoinput',
        handlers: {},
        call: this.call,
      }),
      audiooutput: new MediaDeviceService({
        deviceKind: 'audiooutput',
        handlers: {},
        call: this.call,
      }),
    };
  }

  async getDevices(): Promise<void> {
    const mediaDevices = await navigator.mediaDevices.enumerateDevices();
    this.devicesServices.audiooutput.getDevices(mediaDevices);
    this.devicesServices.audioinput.getDevices(mediaDevices);
    this.devicesServices.videoinput.getDevices(mediaDevices);
  }

  async init(): Promise<IDevices> {
    window.navigator.mediaDevices.addEventListener('devicechange', this.onDeviceChange);

    const defaultDevices = await getDefaultDevices();

    this.getDevices();
    const initialDevices = this.call.mediaDevices.currentDeviceIds;
    let isNeedToSetDevices = false;

    const devices = Object.keys(initialDevices).reduce((acc, key: MediaDeviceKind) => {
      if (!initialDevices[key] && defaultDevices[key] !== 'default') {
        acc[key] = defaultDevices[key];
        isNeedToSetDevices = true;
      } else {
        acc[key] = initialDevices[key];
      }
      return acc;
    }, {} as IDevices);

    if (isNeedToSetDevices) {
      this.call.mediaDevices.setCurrentDevicesIds(devices);
    }

    return devices;
  }

  async initAudioDevices(): Promise<void> {
    const defaultDevices = await getDefaultDevices();
    this.call.mediaDevices.setCurrentDevicesIds({
      audioinput: defaultDevices.audioinput,
      audiooutput: defaultDevices.audiooutput,
      videoinput: this.call.mediaDevices.currentDeviceIds.videoinput,
    });
    await this.getDevices();
  }

  private onDeviceChange = (): void => {
    this.checkChangeDevices();
  };

  checkMicrophoneEnded = debounce(async (deviceId: string): Promise<void> => {
    const devicesList = await navigator.mediaDevices.enumerateDevices();
    const activeDevice = devicesList.find((item) => item.deviceId === deviceId) || null;

    if (activeDevice) {
      this.call.mediaDevices.setCurrentDeviceId('audioinput', activeDevice.deviceId);
      await this.stopHoldMicrophone();
      this.parent.audioVideoStreamer.changeMicrophone(activeDevice.deviceId);
    }

    await this.getDevices();
  }, 1000);

  checkChangeDevices = debounce(async (_deviceId?: string): Promise<void> => {
    const devices = await getDefaultDevices();
    if (this.call.mediaDevices.currentDeviceIds.audioinput !== devices.audioinput) {
      this.call.mediaDevices.setCurrentDeviceId('audioinput', devices.audioinput);
      await this.stopHoldMicrophone();
      this.parent.audioVideoStreamer.changeMicrophone(devices.audioinput);
    }

    if (
      this.call.mediaDevices.currentDeviceIds.audiooutput !== devices.audiooutput &&
      devices.audiooutput
    ) {
      this.call.mediaDevices.setCurrentDeviceId('audiooutput', devices.audiooutput);
      await this.parent.audioVideoStreamer.changeSpeaker(devices.audiooutput);
    }

    if (this.call.mediaDevices.currentDeviceIds.videoinput !== devices.videoinput) {
      this.call.mediaDevices.setCurrentDeviceId('videoinput', devices.videoinput);

      // камера не готова к работе (Safari)
      if (devices.videoinput === null) {
        return;
      }

      if (this.call.meStore.isVideoMuted) {
        await this.parent.audioVideoStreamer.changeCamera(devices.videoinput, false);
      } else {
        this.call.meStore.setIsVideoMuted(true);
        await this.parent.audioVideoStreamer.changeCamera(devices.videoinput, true);
        this.call.meStore.setIsVideoMuted(false);
      }
    }

    await this.getDevices();
  }, 1000);

  async stopHoldMicrophone(): Promise<void> {
    if (this.holdMicrophonePromise) {
      const stream = await this.holdMicrophonePromise;
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      this.holdMicrophonePromise = null;
    }
  }

  // https://www.macworld.com/article/3538798/mac-bluetooth-audio-quality-ways-to-fix-it.html
  async holdMicrophone(deviceId?: string): Promise<void> {
    if (this.holdMicrophonePromise) {
      return;
    }
    try {
      this.holdMicrophonePromise = window.navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId } : true,
        video: false,
      });
    } catch (e) {
      console.warn(e);
    }
  }

  async setCurrentDeviceId(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    this.call.mediaDevices.setCurrentDeviceId(kind, deviceId);
    if (kind === 'audioinput') {
      await this.stopHoldMicrophone();
      this.parent.audioVideoStreamer.changeMicrophone(deviceId);
    } else if (kind === 'audiooutput') {
      await this.parent.audioVideoStreamer.changeSpeaker(deviceId);
    } else if (kind === 'videoinput') {
      if (
        this.call.meStore.isVideoMuted &&
        this.call.maxBroadcasters >= this.call.opponents.opponentsPublishers.length
      ) {
        await this.parent.audioVideoStreamer.changeCamera(deviceId, false);
      } else if (this.call.meStore.isVideoMuted) {
        await this.parent.audioVideoStreamer.changeCamera(deviceId, false);
        await this.parent.audioVideoStreamer.unmuteVideo(true);
        this.call.meStore.setIsVideoMuted(false);
      } else {
        this.call.meStore.setIsVideoMuted(true);
        await this.parent.audioVideoStreamer.changeCamera(deviceId, true);
        this.call.meStore.setIsVideoMuted(false);
      }
    }
  }

  dispose(): void {
    this.stopHoldMicrophone();
    window.navigator.mediaDevices.removeEventListener('devicechange', this.onDeviceChange);
  }
}
